// src/Pages/VoterVerifiedPage.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { getAllElections } from "../services/ElectionService";

const VoterVerifiedPage = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const voterDetails = state?.voterDetails;
    const [electionName, setElectionName] = useState("");

    useEffect(() => {
        if (voterDetails && voterDetails.electionId) {
            getAllElections()
                .then((elections) => {
                    const election = elections.find(
                        (e) => e.id === voterDetails.electionId
                    );
                    if (election) {
                        setElectionName(election.name);
                    }
                })
                .catch((err) => console.error("Error fetching elections", err));
        }
    }, [voterDetails]);

    if (!voterDetails) {
        return (
            <div className="flex items-center justify-center h-screen bg-bgColor">
                <div className="p-8 bg-white rounded-xl shadow-lg">
                    <p className="text-gray-700 text-center">
                        No voter details available. Please scan your QR code.
                    </p>
                    <button
                        onClick={() => navigate("/voterScan")}
                        className="mt-6 py-2 px-6 bg-[#154c79] bg-opacity-90 text-white font-semibold rounded-lg shadow-md hover:bg-black-700 transition w-full"
                    >
                        Go to Scanner
                    </button>
                </div>
            </div>
        );
    }

    // Handle "Next": mark the voter as having voted.
    const handleNext = async () => {
        try {
            await axios.put(`https://backend.block-ballot.com/voters/vote/${voterDetails.id}`, null, {
                params: { electionIdFromQR: voterDetails.electionId },
            });
            navigate("/ballotSheetPage", { state: { voterDetails } });
        } catch (error) {
            if (error.response && error.response.status === 400) {
                alert(
                    "You have already voted. If this is a mistake, please contact the administrator."
                );
                navigate("/voterScan", { state: { error: "User has already voted" } });
            } else {
                alert("An error occurred while processing your vote. Please try again later.");
            }
        }
    };

    // Handle "Scan Again": navigate back to the QR scanning page.
    const handleScanAgain = () => {
        navigate("/voterScan");
    };

    return (
        <div className="flex flex-col min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
            {/* Header */}
            <div className="pt-8 pb-4 px-6">
                <h1 className="text-3xl font-bold text-center text-black">
                    Voter Verified
                    <div className="h-1 w-24 bg-black mx-auto mt-2 rounded-full"></div>
                </h1>
            </div>

            {/* Main Content */}
            <div className="flex-grow flex justify-center items-center px-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-lg border border-purple-100">
                    {/* Voter Badge */}
                    <div className="mb-6 p-4 bg-gradient-to-r from-purple-100 to-blue-100 rounded-xl">
                        <div className="flex items-center justify-between">
                            <div className="font-semibold text-blue-900 text-xl">
                                {voterDetails.name || "N/A"}
                            </div>
                            <div className="px-3 py-1 bg-green-500 text-white text-sm rounded-full font-medium">
                                {voterDetails.status || "Active"}
                            </div>
                        </div>
                        <div className="text-gray-600 text-sm mt-1">
                            ID: {voterDetails.id || "N/A"}
                        </div>
                        <div className="text-gray-600 text-sm mt-1">
                            Election ID: {voterDetails.electionId || "N/A"}
                        </div>
                    </div>

                    {/* Details Grid: Election replaces Department */}
                    <div className="grid grid-cols-2 gap-6 mb-6">
                        <div className="p-4 bg-blue-50 rounded-lg">
                            <p className="text-sm text-blue-900 mb-1">Election</p>
                            <p className="font-semibold text-black">
                                {electionName || "N/A"}
                            </p>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-lg">
                            <p className="text-sm text-blue-900 mb-1">Position</p>
                            <p className="font-semibold text-black">
                                {voterDetails.position || voterDetails.designation || "N/A"}
                            </p>
                        </div>
                        <div className="p-4 bg-indigo-50 rounded-lg">
                            <p className="text-sm text-blue-900 mb-1">Date</p>
                            <p className="font-semibold text-black">
                                {voterDetails.date || new Date().toLocaleDateString()}
                            </p>
                        </div>
                        <div className="p-4 bg-violet-50 rounded-lg">
                            <p className="text-sm text-blue-900 mb-1">Time</p>
                            <p className="font-semibold text-black">
                                {voterDetails.time || new Date().toLocaleTimeString()}
                            </p>
                        </div>
                    </div>

                    {/* Additional Info */}
                    <div className="space-y-3 mb-6 p-4 bg-gray-50 rounded-lg">
                        <p className="font-medium text-gray-600">
                            Email: <span className="text-gray-900">{voterDetails.email || "N/A"}</span>
                        </p>
                        <p className="font-medium text-gray-600">
                            Gender: <span className="text-gray-900">{voterDetails.gender || "N/A"}</span>
                        </p>
                    </div>

                    {/* Buttons */}
                    <div className="mt-8 grid grid-cols-2 gap-4 pb-2">
                        <button
                            onClick={handleNext}
                            className="py-3 px-6 bg-gray-700 text-white font-semibold rounded-lg shadow-md hover:bg-gray-600 transition flex items-center justify-center"
                        >
                            <span>Next</span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fillRule="evenodd"
                                    d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>
                        <button
                            onClick={handleScanAgain}
                            className="py-3 px-6 bg-gray-200 text-gray-700 font-semibold rounded-lg shadow-md hover:bg-gray-300 transition flex items-center justify-center"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fillRule="evenodd"
                                    d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 110 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            <span>Scan Again</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VoterVerifiedPage;
